import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { DeltaLine } from './DeltaLine';
import { formatReportValue } from '../../utils/reportFormat';
import type { ComparedFigure, MonthlyClubReport, ReportValueFormat } from '../../types/monthlyClubReport';
interface ReportEmailPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  report: MonthlyClubReport;
  wineryName: string;
}
export function ReportEmailPreviewModal({
  isOpen,
  onClose,
  report,
  wineryName
}: ReportEmailPreviewModalProps) {
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  const {
    members,
    signups,
    cancellations,
    revenue
  } = report.headline;
  const growth = members.value - members.prev;
  const tiles: {
    label: string;
    figure: ComparedFigure;
    format: ReportValueFormat;
    inverse?: boolean;
  }[] = [{
    label: 'Active members',
    figure: members,
    format: 'number'
  }, {
    label: 'New sign-ups',
    figure: signups,
    format: 'number'
  }, {
    label: 'Cancellations',
    figure: cancellations,
    format: 'number',
    inverse: true
  }, {
    label: 'Club revenue',
    figure: revenue,
    format: 'currency'
  }];
  const growthText = growth > 0 ? `so your club grew by ${growth.toLocaleString('en-AU')}` : growth < 0 ? `so your club shrank by ${Math.abs(growth).toLocaleString('en-AU')}` : 'so your club held steady';
  return <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4" data-id="element-3614" data-mp="3lfq5r3k5bq0e">
      <div className="absolute inset-0 bg-[#0F172A]/50 backdrop-blur-sm" onClick={onClose} data-id="element-3615" data-mp="3gngd3frncbuf" />
      <div role="dialog" aria-modal="true" aria-labelledby="email-preview-title" className="relative w-full max-w-[720px] bg-white rounded-lg shadow-[0_20px_25px_rgba(15,23,42,0.15)] flex flex-col max-h-[90vh]" data-id="element-3616" data-mp="2sxvoag8takd8">
        <div className="px-6 pt-6 pb-4 border-b border-[#E2E8F0] flex items-start justify-between flex-shrink-0" data-id="element-3617" data-mp="rwxtefbs9o6m">
          <div className="min-w-0" data-id="element-3618" data-mp="wty0mtkdcwzb">
            <h2 id="email-preview-title" className="text-[20px] font-semibold text-[#0F172A] leading-tight" data-id="element-3619" data-mp="wswd3pm62i0p">
              Email preview
            </h2>
            <dl className="mt-2 text-[13px] grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5" data-id="element-3620" data-mp="16k0s2pbue2vj">
              <dt className="text-[#94A3B8]" data-id="element-3621" data-mp="8y3bwykxq9mr">From</dt>
              <dd className="text-[#0F172A]" data-id="element-3622" data-mp="8zmuwwxz6ys8">WinePik</dd>
              <dt className="text-[#94A3B8]" data-id="element-3623" data-mp="dx69jxyl5dkp">Subject</dt>
              <dd className="text-[#0F172A]" data-id="element-3624" data-mp="ivhmihk6g475">
                Your {report.monthLabel} club report · {wineryName}
              </dd>
            </dl>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-[#94A3B8] hover:text-[#0F172A] hover:bg-[#F1F5F9] rounded-md transition-colors" aria-label="Close" data-id="element-3625" data-mp="mye8mi8xeifu">
            <X className="w-5 h-5" data-id="element-3626" data-mp="1yb6sz7ze04lv" />
          </button>
        </div>

        <div className="overflow-y-auto bg-[#F1F5F9] p-6" data-id="element-3627" data-mp="3iu0ugieg2l24">
          <div className="max-w-[600px] mx-auto bg-white rounded-lg border border-[#E2E8F0] p-8" data-id="element-3628" data-mp="3ithhzh7kcovh">
            <div className="text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-3629" data-mp="3etgxpfxs9jgi">
              {wineryName}
            </div>
            <h3 className="text-[24px] font-semibold text-[#0F172A] mt-1" data-id="element-3630" data-mp="353dy2ww5p797">
              {report.monthLabel} club report
            </h3>

            <div className="grid grid-cols-2 gap-3 mt-6" data-id="element-3631" data-mp="305cgkgedpfzu">
              {tiles.map(tile => <div key={tile.label} className="border border-[#E2E8F0] rounded-lg p-4" data-id="element-3632" data-mp="2v3n20qry6k70">
                  <div className="text-[12px] font-semibold text-[#94A3B8] uppercase tracking-[0.5px]" data-id="element-3633" data-mp="2r2bb8hxc7sx8">
                    {tile.label}
                  </div>
                  <div className="text-[24px] font-semibold text-[#0F172A] mt-1 leading-tight" data-id="element-3634" data-mp="2m6csb9us3347">
                    {formatReportValue(tile.figure.value, tile.format)}
                  </div>
                  <div className="mt-1.5" data-id="element-3635" data-mp="kakcbeqlsbm5">
                    <DeltaLine mode="change" current={tile.figure.value} compared={tile.figure.prev} label={report.prevShort} format={tile.format} inverse={tile.inverse} data-id="element-3636" data-mp="3d6vlfrmjqakt" />
                  </div>
                </div>)}
            </div>

            <p className="text-[15px] text-[#475569] mt-6 leading-relaxed" data-id="element-3637" data-mp="345xh1887411p">
              {signups.value.toLocaleString('en-AU')} new members joined and{' '}
              {cancellations.value.toLocaleString('en-AU')} cancelled,{' '}
              {growthText} to {members.value.toLocaleString('en-AU')} members.
            </p>

            <div className="mt-6" data-id="element-3638" data-mp="2lfxsqnk6ipzc">
              <button type="button" onClick={onClose} className="inline-flex items-center justify-center h-11 px-6 rounded-md bg-[#0EA5E9] text-white text-[14px] font-semibold hover:bg-[#0284C7] transition-colors" data-id="element-3639" data-mp="2lgqsv5omuh8i">
                View full report
              </button>
            </div>

            <p className="text-[12px] text-[#94A3B8] mt-8 pt-6 border-t border-[#E2E8F0]" data-id="element-3640" data-mp="2ghe42fe7b03a">
              You're receiving this because you're a report recipient for{' '}
              {wineryName}. Change report settings in WinePik.
            </p>
          </div>
        </div>
      </div>
    </div>;
}